import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { rupiah, suggestPrice, CATEGORY_MARGIN } from "@/lib/format";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

function EditablePrice() { return null; }

export default function PricingCheckTab() {
  const [products, setProducts] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [hist, setHist] = useState(null);
  const [histQ, setHistQ] = useState("");
  const [histUser, setHistUser] = useState("");
  const tableRef = useRef(null);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});
  const [rowBusy, setRowBusy] = useState(false);

  const parseD = (s) => (s === "" || s == null ? 0 : Math.max(0, parseInt(s, 10) || 0));
  const startEdit = (p) => {
    setEditId(p.id);
    setDraft({ stock: String(p.stok ?? ""), unit: p.unit || "pcs", harga_modal: String(p.modal || ""), price: String(p.price || "") });
  };
  const saveRow = async (p) => {
    setRowBusy(true);
    try {
      const body = {};
      const modal = parseD(draft.harga_modal);
      const price = parseD(draft.price);
      const unit = (draft.unit || "pcs").trim().toLowerCase() || "pcs";
      if (modal !== p.modal) body.harga_modal = modal;
      if (price !== p.price) body.price = price;
      if (unit !== (p.unit || "pcs")) body.unit = unit;
      if (branchId) {
        const st = parseD(draft.stock);
        if (st !== p.stok) { body.stock = st; body.branch_id = branchId; }
      }
      if (Object.keys(body).length) await api.patch(`/admin/products/${p.id}/pricing`, body);
      await api.post(`/admin/products/${p.id}/lock-pricing`);
      setProducts((ps) => ps.map((x) => {
        if (x.id !== p.id) return x;
        const next = { ...x, price_locked: true };
        if ("harga_modal" in body) next.harga_modal = body.harga_modal;
        if ("price" in body) next.price = body.price;
        if ("unit" in body) next.unit = body.unit;
        if ("stock" in body) next.stock_by_branch = { ...(x.stock_by_branch || {}), [branchId]: body.stock };
        return next;
      }));
      toast.success(`${p.name} tersimpan & dikunci 🔒 — staff/kasir tidak bisa ubah harga lagi`);
      setEditId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setRowBusy(false); }
  };
  const unlockRow = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/unlock-pricing`);
      setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, price_locked: false } : x)));
      toast.success(`${p.name}: kunci dibuka 🔓 — staff/kasir bisa ubah lagi`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka kunci"); }
  };
  const toggleOwner = async (p) => {
    const next = !p.price_owner;
    try {
      await api.patch(`/admin/products/${p.id}/pricing`, { price_owner: next });
      setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, price_owner: next } : x)));
      toast.success(next ? `${p.name} ditandai ★ Owner — badge "Di bawah saran" diganti & notifikasi harian diperbarui` : `${p.name}: tanda ★ Owner dilepas — kembali dievaluasi margin kategori`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  useEffect(() => {
    if (!showHist || hist !== null) return;
    api.get("/admin/price-history?limit=300").then((r) => setHist(r.data)).catch(() => toast.error("Gagal memuat riwayat"));
  }, [showHist]);

  const load = () => {
    Promise.all([api.get("/products"), api.get("/branches")])
      .then(([p, b]) => { setProducts(p.data); setBranches(b.data); })
      .catch(() => toast.error("Gagal memuat data"));
  };
  useEffect(() => { load(); }, []);

  const saran = (category, modal) => suggestPrice(category, modal);

  const rows = useMemo(() => {
    if (!products) return [];
    return products
      .map((p) => {
        const modal = p.harga_modal || 0;
        const price = p.price || 0;
        const stok = branchId ? (p.stock_by_branch?.[branchId] ?? 0) : Object.values(p.stock_by_branch || {}).reduce((a, b) => a + (b || 0), 0) || p.stock || 0;
        const sr = saran(p.category, modal);
        const profit = (price - modal) * stok;
        let status = "ok";
        if (modal <= 0 || price <= 0) status = "kosong";
        else if (price < modal) status = "rugi";
        else if (p.price_owner) status = "owner";
        else if (price < sr) status = "tipis";
        return { ...p, modal, price, stok, sr, profit, status };
      })
      .filter((p) => (!q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.barcode || "").includes(q)))
      .filter((p) => (!cat || p.category === cat))
      .filter((p) => (!onlyIssues || (p.status !== "ok" && p.status !== "owner")))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products, q, cat, branchId, onlyIssues]);

  const totalModal = rows.reduce((a, p) => a + p.modal * p.stok, 0);
  const totalProfit = rows.reduce((a, p) => a + p.profit, 0);
  const issueCount = useMemo(() => rows.filter((p) => p.status !== "ok" && p.status !== "owner").length, [rows]);
  const cats = useMemo(() => [...new Set((products || []).map((p) => p.category))].sort(), [products]);

  const STATUS_LABEL = { ok: "✓ Sesuai", kosong: "⚠ Harga/modal kosong", rugi: "✗ Jual di bawah modal", tipis: "⚠ Di bawah saran", owner: "★ Owner" };
  const STATUS_CLS = { ok: "bg-emerald-100 text-emerald-800", kosong: "bg-stone-200 text-stone-600", rugi: "bg-rose-100 text-rose-700", tipis: "bg-amber-100 text-amber-800", owner: "bg-sky-100 text-sky-800" };

  return (
    <div className="space-y-4" data-testid="pricing-check-tab">
      <div>
        <h3 className="font-display font-bold text-xl">💲 Cek Modal & Harga Jual</h3>
        <p className="text-xs text-stone-500 mt-0.5">Kontrol inputan staff: klik ✏️ Edit → ubah angka → 💾 Simpan untuk <b>mengunci</b> produk 🔒 — setelah dikunci, staff input & kasir tidak bisa mengubah harganya lagi (stok tetap bisa). Saran harga mengikuti <b>margin per kategori</b> (Sembako 6% · Mie Instan 7% · Ibu & Bayi 8% · Bumbu & Saus 12% · Kebersihan Rumah 14% · Perawatan Diri 16% · Minuman & Snack 18% · Obat 20% · Perlengkapan Rumah & ATK 25%), dibulatkan ke Rp100 terdekat. Saran tidak mengubah harga yang sudah diinput.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input data-testid="pricing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama/barcode…" className="pl-9 pr-8 py-2 rounded-full border border-[#E5DECF] bg-white text-sm w-56 outline-none focus:ring-2 focus:ring-emerald-600" />
          {q && <button data-testid="pricing-search-clear" onClick={() => setQ("")} title="Hapus pencarian" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition"><X className="w-3 h-3" /></button>}
        </div>
        <select data-testid="pricing-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
          <option value="">Semua Kategori</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="pricing-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
          <option value="">Stok: Semua Cabang</option>
          {branches.map((b) => <option key={b.id} value={b.id}>Stok: {b.name}</option>)}
        </select>
        <label data-testid="pricing-only-issues" className="text-xs font-bold text-stone-600 flex items-center gap-1.5 bg-white rounded-full border border-[#E5DECF] px-3 py-2 cursor-pointer">
          <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} className="w-4 h-4 accent-rose-600" /> Hanya bermasalah {issueCount > 0 && <span className="text-rose-600">({issueCount})</span>}
        </label>
      </div>

      {!products && <p className="text-sm text-stone-400">Memuat…</p>}
      {products && (
        <div className="space-y-2">
          <div className="flex justify-end gap-1.5">
            <button type="button" data-testid="pricing-scroll-left" onClick={() => tableRef.current?.scrollBy({ left: -600, behavior: "smooth" })} className="w-8 h-8 rounded-full bg-stone-900 text-white text-sm font-black hover:bg-emerald-700 transition">←</button>
            <button type="button" data-testid="pricing-scroll-right" onClick={() => tableRef.current?.scrollBy({ left: 600, behavior: "smooth" })} className="w-8 h-8 rounded-full bg-stone-900 text-white text-sm font-black hover:bg-emerald-700 transition">→</button>
          </div>
          <div ref={tableRef} className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto">
          <table className="w-full text-xs min-w-[1150px]">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-white bg-rose-600">
                <th className="p-2">No</th><th className="p-2">Kategori</th><th className="p-2">Nama Produk</th>
                <th className="p-2 text-right">Stok ✏️</th><th className="p-2 text-center">Satuan ✏️</th>
                <th className="p-2 text-right">Harga Beli (Modal) ✏️</th><th className="p-2 text-right">Total Modal</th>
                <th className="p-2 text-right">Harga Jual ✏️</th><th className="p-2 text-right">Margin</th>
                <th className="p-2 text-right">Harga Jual Saran</th><th className="p-2 text-right">Perkiraan Profit</th><th className="p-2">Status</th><th className="p-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={p.id} data-testid={`pricing-row-${p.id}`} className={`border-b border-stone-100 ${editId === p.id ? "bg-emerald-50/70" : i % 2 ? "bg-sky-50/60" : ""}`}>
                  <td className="p-2 text-stone-400">{i + 1}</td>
                  <td className="p-2 text-[11px] text-stone-500 whitespace-nowrap">{p.category}</td>
                  <td className="p-2 font-semibold max-w-[240px]"><div className="truncate">{p.name}{p.price_locked ? <span data-testid={`pricing-locked-${p.id}`} title="Dikunci super admin — staff/kasir tidak bisa ubah harga" className="ml-1">🔒</span> : null}</div>{p.barcode && <div className="text-[10px] font-mono text-stone-400">{p.barcode}</div>}</td>
                  <td className="p-2 text-right bg-sky-50/70">
                    {editId === p.id && branchId ? (
                      <input data-testid={`pricing-stock-${p.id}`} type="text" inputMode="numeric" value={draft.stock} onChange={(e) => setDraft((d) => ({ ...d, stock: e.target.value.replace(/[^0-9]/g, "") }))} className="w-16 text-right rounded-lg border border-emerald-400 bg-white px-1.5 py-1 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-600" />
                    ) : (
                      <span className="font-bold" title={branchId ? "" : "Pilih cabang di filter atas untuk mengubah stok"}>{p.stok}</span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {editId === p.id ? (
                      <input data-testid={`pricing-unit-${p.id}`} type="text" value={draft.unit} onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))} className="w-16 text-center rounded-lg border border-emerald-400 bg-white px-1 py-1 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-600" />
                    ) : (
                      <span className="text-stone-500">{p.unit || "pcs"}</span>
                    )}
                  </td>
                  <td className="p-2 text-right bg-amber-50/70">
                    {editId === p.id ? (
                      <input data-testid={`pricing-modal-${p.id}`} type="text" inputMode="numeric" value={draft.harga_modal} onChange={(e) => setDraft((d) => ({ ...d, harga_modal: e.target.value.replace(/[^0-9]/g, "") }))} className="w-24 text-right rounded-lg border border-emerald-400 bg-white px-1.5 py-1 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-600" />
                    ) : (
                      <span className={`font-bold ${p.modal <= 0 ? "text-amber-600" : ""}`}>{p.modal ? rupiah(p.modal) : "—"}</span>
                    )}
                  </td>
                  <td className="p-2 text-right">{rupiah(p.modal * p.stok)}</td>
                  <td className="p-2 text-right">
                    {editId === p.id ? (
                      <input data-testid={`pricing-price-${p.id}`} type="text" inputMode="numeric" value={draft.price} onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value.replace(/[^0-9]/g, "") }))} className="w-24 text-right rounded-lg border border-emerald-400 bg-white px-1.5 py-1 text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-600" />
                    ) : (
                      <span className={`font-bold ${["kosong", "rugi", "tipis"].includes(p.status) ? "text-amber-600" : ""}`}>{p.price ? rupiah(p.price) : "—"}</span>
                    )}
                  </td>
                  <td className={`p-2 text-right font-bold ${p.modal > 0 && p.price >= p.modal ? "text-emerald-700" : "text-rose-600"}`}>{p.modal > 0 ? `${(((p.price - p.modal) / p.modal) * 100).toFixed(1)}%` : "—"}</td>
                  <td className="p-2 text-right text-stone-500">{p.sr ? <span>{rupiah(p.sr)}<span className="block text-[9px] text-stone-400">margin {CATEGORY_MARGIN[p.category] ?? 15}%</span></span> : "—"}</td>
                  <td className={`p-2 text-right font-bold ${p.profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{rupiah(p.profit)}</td>
                  <td className="p-2"><span data-testid={`pricing-status-${p.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_CLS[p.status]}`}>{STATUS_LABEL[p.status]}</span></td>
                  <td className="p-2 whitespace-nowrap">
                    {editId === p.id ? (
                      <>
                        <button data-testid={`pricing-save-${p.id}`} onClick={() => saveRow(p)} disabled={rowBusy} className="px-2.5 py-1 rounded-full bg-emerald-700 text-white text-[10px] font-bold hover:bg-emerald-800 disabled:opacity-50">{rowBusy ? "…" : "💾 Simpan"}</button>
                        <button data-testid={`pricing-cancel-${p.id}`} onClick={() => setEditId(null)} className="ml-1 px-2 py-1 rounded-full bg-stone-200 text-stone-600 text-[10px] font-bold hover:bg-stone-300">Batal</button>
                      </>
                    ) : (
                      <>
                        <button data-testid={`pricing-edit-${p.id}`} onClick={() => startEdit(p)} className="px-2.5 py-1 rounded-full bg-stone-900 text-white text-[10px] font-bold hover:bg-emerald-700 transition">✏️ Edit</button>
                        {(p.price_owner || p.status === "tipis") && (
                          <button data-testid={`pricing-owner-${p.id}`} onClick={() => toggleOwner(p)} title={p.price_owner ? "Lepas tanda Harga Owner — kembali dievaluasi margin kategori" : "Tandai harga ini ditetapkan owner (sengaja tidak ikut saran)"} className={`ml-1 px-2 py-1 rounded-full text-[10px] font-bold transition ${p.price_owner ? "bg-sky-600 text-white hover:bg-sky-700" : "bg-sky-100 text-sky-800 hover:bg-sky-200"}`}>{p.price_owner ? "★ Owner ✓" : "★ Owner"}</button>
                        )}
                        {p.price_locked ? <button data-testid={`pricing-unlock-${p.id}`} onClick={() => unlockRow(p)} title="Buka kunci agar staff/kasir bisa ubah harga lagi" className="ml-1 px-1.5 py-1 rounded-full bg-amber-100 text-[10px] font-bold hover:bg-amber-200">🔓</button> : null}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={13} className="p-6 text-center text-sm text-stone-400">Tidak ada produk cocok filter.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-stone-900 text-white text-xs font-bold">
                  <td className="p-2" colSpan={6}>Total ({rows.length} produk)</td>
                  <td className="p-2 text-right" data-testid="pricing-total-modal">{rupiah(totalModal)}</td>
                  <td className="p-2" colSpan={3}></td>
                  <td className="p-2 text-right">Profit Bruto</td>
                  <td className="p-2 text-right text-emerald-300" data-testid="pricing-total-profit">{rupiah(totalProfit)}</td>
                  <td className="p-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E5DECF]">
        <button data-testid="pricing-history-toggle" onClick={() => setShowHist(!showHist)} className="w-full text-left p-4 font-display font-bold text-base flex items-center justify-between">
          <span>🕓 Riwayat Perubahan Harga</span>
          <span className="text-stone-400 text-xs font-sans">{showHist ? "▲ tutup" : "▼ buka"}</span>
        </button>
        {showHist && (
          <div className="px-4 pb-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <input data-testid="pricing-history-q" value={histQ} onChange={(e) => setHistQ(e.target.value)} placeholder="Filter produk…" className="px-3 py-1.5 rounded-full border border-[#E5DECF] bg-white text-xs w-44 outline-none focus:ring-2 focus:ring-emerald-600" />
              <select data-testid="pricing-history-user" value={histUser} onChange={(e) => setHistUser(e.target.value)} className="text-xs border border-[#E5DECF] rounded-full px-3 py-1.5 bg-white">
                <option value="">Semua Pengguna</option>
                {[...new Set((hist || []).map((h) => h.changed_by))].filter(Boolean).map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            {!hist && <p className="text-xs text-stone-400">Memuat riwayat…</p>}
            {hist && !hist.length && <p data-testid="pricing-history-empty" className="text-xs text-stone-400">Belum ada perubahan harga tercatat.</p>}
            {hist && hist.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[720px]">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]">
                      <th className="p-2">Waktu</th><th className="p-2">Produk</th><th className="p-2">Field</th><th className="p-2 text-right">Lama → Baru</th><th className="p-2">Oleh</th><th className="p-2">Sumber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist
                      .filter((h) => (!histQ || h.product_name.toLowerCase().includes(histQ.toLowerCase())))
                      .filter((h) => (!histUser || h.changed_by === histUser))
                      .map((h) => (
                        <tr key={h.id} data-testid={`pricing-history-row-${h.id}`} className="border-b border-stone-100 last:border-0">
                          <td className="p-2 whitespace-nowrap text-stone-500">{new Date(h.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                          <td className="p-2 font-semibold max-w-[220px] truncate">{h.product_name}</td>
                          <td className="p-2">{h.field === "harga_modal" ? "Modal" : "Jual"}</td>
                          <td className="p-2 text-right font-mono font-bold">{rupiah(h.old_value)} → <span className={h.new_value > h.old_value ? "text-emerald-700" : "text-rose-600"}>{rupiah(h.new_value)}</span></td>
                          <td className="p-2 text-stone-500">{h.changed_by}</td>
                          <td className="p-2"><span className="tag-pill bg-stone-100 text-stone-600">{{ cek_modal: "💲 Cek Modal", edit_produk: "✏️ Edit Produk", staff_input: "🧾 Staff Input", import_excel: "📥 Import Excel" }[h.source] || h.source}</span></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
